import { useRef, useState } from 'react';
import { calculateLoan } from 'shared/calculate-loan';
import { formatPrice } from 'shared/utils';

import { UnderlinedText } from '@/components/UnderlinedText/UnderlinedText.tsx';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import BankIdIcon from '@/pages/MyScoopr/pages/Login/_components/ChooseAuthProvider/BankIdIcon.svg?react';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import { CalculatorInput } from './calculator-input.tsx';

const DEFAULT_LOAN_AMOUNT = 100000;
const DEFAULT_PAYBACK_YEARS = 5;

export const Calculator = () => {
  const { toggleChat } = useBrainSession();

  const [loanValue, setLoanValue] = useState(DEFAULT_LOAN_AMOUNT);
  const [paybackTimeValue, setPaybackTimeValue] = useState(DEFAULT_PAYBACK_YEARS);
  const usedFields = useRef(new Set<'amount' | 'years'>());

  const trackCalculatorUsed = (field: 'amount' | 'years') => {
    if (usedFields.current.has(field)) {
      return;
    }

    usedFields.current.add(field);
    sendUserPortalEvent('consumer_loan_calculator_used', { field });
  };

  const results = calculateLoan({
    years: paybackTimeValue,
    months: 0,
    interestRate: 11.5,
    installment: 0,
    fee: 40,
    amount: loanValue,
    establishmentFee: 760,
    establishmentFeeIncluded: false,
    futureValue: 0,
    calculationMode: 'pmt',
  });

  return (
    <div className="flex flex-col rounded-2xl border border-v1-background-80 bg-v1-background-60 p-4 py-6 text-left typography-v1-body4 md:p-8 md:py-10">
      <CalculatorInput
        value={loanValue}
        setValue={(value) => {
          if (value !== loanValue) {
            trackCalculatorUsed('amount');
          }
          setLoanValue(value);
        }}
        label="Hvor mye vil du låne? (10 000 - 600 000 kr)"
        min={10000}
        max={600000}
        step={1000}
        scale="log"
        endAdornment={<span className="typography-v1-h5">kr</span>}
        className="mb-6"
        formatValue={(value) => formatPrice(value, 0)}
      />
      <CalculatorInput
        value={paybackTimeValue}
        setValue={(value) => {
          if (value !== paybackTimeValue) {
            trackCalculatorUsed('years');
          }
          setPaybackTimeValue(value);
        }}
        label="Hvor lang tid vil du betale tilbake lånet? (1 - 5 år)"
        min={1}
        max={5}
        step={1}
        endAdornment={<span className="typography-v1-h5">år</span>}
        className="mb-6"
        formatValue={(value) => `${value} år`}
      />

      <div className="mb-8 flex flex-row items-center justify-between">
        <p>Beregnet månedskostnad</p>
        <UnderlinedText distance="1px">
          <p className="typography-v1-h4">{formatPrice(results.installment ?? 0, 0)}/mnd</p>
        </UnderlinedText>
      </div>

      <dl className="mb-8 flex justify-between">
        <div className="flex flex-col">
          <dt className="text-v1-neutral-100">Effektiv rente</dt>
          <dd className="font-bold">{results.effectiveRate}%</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-v1-neutral-100">Kostnad</dt>
          <dd className="font-bold">{formatPrice((results.total ?? 0) - loanValue, 0)}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-v1-neutral-100">Totalkostnad</dt>
          <dd className="font-bold">{formatPrice(results.total ?? 0, 0)}</dd>
        </div>
      </dl>

      <Button
        color="blue"
        className="mb-6"
        action={() => {
          sendUserPortalEvent('consumer_loan_calculator_cta_clicked', {
            loan_amount: loanValue,
            payback_years: paybackTimeValue,
            calculator_modified:
              loanValue !== DEFAULT_LOAN_AMOUNT || paybackTimeValue !== DEFAULT_PAYBACK_YEARS,
          });
          toggleChat({
            goal: 'consumer_loan',
            step: 'init_from_calculator',
            clientKnowledge: {
              total_amount: loanValue,
              downpayment_time: paybackTimeValue,
            },
          });
        }}
      >
        <span className="inline-flex items-center">
          Søk med
          <BankIdIcon className="ml-[0.65ch] h-[0.7em] w-auto" aria-label="BankID" />
        </span>
      </Button>

      <div className="text-center typography-v1-caption1 text-v1-neutral-100">
        <p>
          Priseksempel: 50.000 over 5 år. Effektiv rente 14,61%. Kostnad 16.960. Totalkostnad
          66.960. Løpetid er fra 0-5år – Høyeste rente er 23,96% og laveste er 7,24% nominell.
        </p>
      </div>
    </div>
  );
};
